import { useRef, useState, useEffect, useCallback } from "react";
import { graphql, useFragment } from "react-relay";
import type { courseCategoryFragment$key } from "./__generated__/courseCategoryFragment.graphql";
import { CoursePreview } from "./course-preview";
import { Icon } from "@/components/icon";

type CourseCategoryProps = {
	category: courseCategoryFragment$key;
};

export function CourseCategory({ category }: CourseCategoryProps) {
	const data = useFragment<courseCategoryFragment$key>(
		graphql`
    fragment courseCategoryFragment on CourseCategory {
      id
      name
      orientation
      courses(status: ACTIVE) {
        id
        ...coursePreviewFragment
      }
    }
  `,
		category,
	);

	const scrollContainerRef = useRef<HTMLDivElement>(null);
	const [canScrollLeft, setCanScrollLeft] = useState(false);
	const [canScrollRight, setCanScrollRight] = useState(false);

	const updateScrollState = useCallback(() => {
		const container = scrollContainerRef.current;
		if (!container) return;

		setCanScrollLeft(container.scrollLeft > 0);
		setCanScrollRight(
			container.scrollLeft < container.scrollWidth - container.clientWidth - 1,
		);
	}, []);

	useEffect(() => {
		const container = scrollContainerRef.current;
		if (!container) return;

		updateScrollState();

		container.addEventListener("scroll", updateScrollState);
		window.addEventListener("resize", updateScrollState);

		return () => {
			container.removeEventListener("scroll", updateScrollState);
			window.removeEventListener("resize", updateScrollState);
		};
	}, [updateScrollState]);

	const scroll = (direction: "left" | "right") => {
		const container = scrollContainerRef.current;
		if (!container) return;

		const scrollAmount = container.clientWidth * 0.8;
		container.scrollBy({
			left: direction === "left" ? -scrollAmount : scrollAmount,
			behavior: "smooth",
		});
	};

	return (
		<div className="flex flex-col gap-3 mb-12 last:mb-6 w-full first:mt-6">
			<div className="px-6 lg:px-16">
				<span className="text-balance font-semibold text-lg/6 text-foreground sm:text-base/6">
					{data.name}
				</span>
			</div>

			<div className="overflow-visible w-full relative">
				{canScrollLeft && (
					<button
						type="button"
						onClick={() => scroll("left")}
						className="hidden lg:block absolute left-4 top-1/2 -translate-y-1/2 z-10 bg-background hover:bg-accent border border-border rounded-full p-2 shadow-md"
						aria-label="Scroll left"
					>
						<Icon name="ChevronLeft" className="size-5" />
					</button>
				)}

				{canScrollRight && (
					<button
						type="button"
						onClick={() => scroll("right")}
						className="hidden lg:block absolute right-4 top-1/2 -translate-y-1/2 z-10 bg-background hover:bg-accent border border-border rounded-full p-2 shadow-md"
						aria-label="Scroll right"
					>
						<Icon name="ChevronRight" className="size-5" />
					</button>
				)}

				<div
					ref={scrollContainerRef}
					className="flex relative flex-row px-5 lg:px-15 gap-1 no-scrollbar items-start justify-start w-full overflow-x-auto overflow-y-visible"
				>
					{data.courses.map((c) => (
						<CoursePreview
							key={c.id}
							orientation={data.orientation}
							course={c}
						/>
					))}
				</div>
			</div>
		</div>
	);
}
